/**
 * Hydrated Experiment — SSR-safe experiment variant access.
 *
 * The server fetches all Amplitude variants and injects them into the HTML
 * via a `<script>` tag (HydratedExperimentProvider). On the client, the data
 * is available synchronously in `window.__HYDRATED_EXPERIMENTS__` before
 * React hydrates, enabling:
 *
 * - Module-level reads:  `getHydratedExperiment('name')`
 * - Imperative calls:    same function in event handlers / utility code
 * - React hook:          `useHydratedExperiment('name')`
 * - Route-level switch:  `dynamicWithExperiment({ ... })`
 *
 * None of the above auto-expose. Consumers must call
 * `reportExperimentExposure` from `experimentNew.ts` manually.
 */
import type { ComponentType, FC, PropsWithoutRef } from 'react'
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { useRouteLoaderData } from 'react-router'
import {
  getExperimentParamSync,
  reportExperimentExposure,
  useExperimentVariant,
} from 'src/utils/experimentNew'
import { createWeakMap } from 'src/utils/map'
import { normalizeModule, type LoadModuleResult } from 'src/utils/ssr'

// ============================================================================
// Global accessor (module-level, imperative, any client-side code)
// ============================================================================

declare global {
  interface Window {
    __HYDRATED_EXPERIMENTS__?: Record<string, string>
  }
}

/**
 * Read a server-injected experiment variant.
 *
 * Works at module top-level, in event handlers, utility functions — anywhere
 * on the client. Returns `undefined` on the server or when no data was
 * injected (e.g. CSR-only mode).
 */
export function getHydratedExperiment(name: string): string | undefined {
  if (typeof window === 'undefined') return undefined
  return window.__HYDRATED_EXPERIMENTS__?.[name]
}

// ============================================================================
// HydratedExperimentProvider (rendered in root Layout <head>)
// ============================================================================

type RootLoaderData = { experiments?: Record<string, string> }

function useRootExperiments(): Record<string, string> | undefined {
  const data = useRouteLoaderData('root') as RootLoaderData | undefined
  return data?.experiments
}

/**
 * Injects server-fetched experiment variants as an IIFE `<script>` tag.
 * The browser executes this script before React hydrates, so
 * `window.__HYDRATED_EXPERIMENTS__` is ready for module-level access.
 *
 * Place inside `<head>` in root.tsx Layout.
 */
export function HydratedExperimentProvider() {
  const experiments = useRootExperiments()

  if (!experiments || Object.keys(experiments).length === 0) {
    return null
  }

  // Escape `<` to prevent `</script>` from breaking the HTML parser
  const safeJson = JSON.stringify(experiments).replace(/</g, '\\u003c')

  return (
    <script
      suppressHydrationWarning
      dangerouslySetInnerHTML={{
        __html: `(function(){window.__HYDRATED_EXPERIMENTS__=${safeJson}})()`,
      }}
    />
  )
}

// ============================================================================
// HydratedExperimentProbe (invisible diagnostic element)
// ============================================================================

/** Module-level cache so React.lazy is called once per experiment name. */
const probeCache = new Map<string, FC>()

function getProbeComponent(experimentName: string): FC {
  let component = probeCache.get(experimentName)
  if (!component) {
    component = dynamicWithExperiment({
      experimentName,
      treatment: () => (
        <span data-exp={experimentName} data-variant="treatment" />
      ),
      control: () => <span data-exp={experimentName} data-variant="control" />,
    })
    probeCache.set(experimentName, component)
  }
  return component
}

interface HydratedExperimentProbeProps {
  /** Experiment names to probe. */
  experiments: string[]
}

/**
 * Invisible probe that exercises the full `dynamicWithExperiment` pipeline
 * for each listed experiment, exposing the resolved variant as data attributes.
 *
 * @example
 * <HydratedExperimentProbe experiments={['exp2604-ssr-root-layout']} />
 * // → <div hidden><span data-exp="exp2604-ssr-root-layout" data-variant="treatment" /></div>
 */
export function HydratedExperimentProbe({
  experiments: experimentNames,
}: HydratedExperimentProbeProps) {
  if (experimentNames.length === 0) return null

  return (
    <div hidden aria-hidden="true" data-testid="experiment-probe">
      {experimentNames.map(name => {
        const Component = getProbeComponent(name)
        return <Component key={name} />
      })}
    </div>
  )
}

// ============================================================================
// useHydratedExperiment hook
// ============================================================================

const noopSubscribe = () => () => {}

/**
 * SSR-safe experiment variant hook.
 *
 * Data source priority:
 * 1. Server-injected data (`window.__HYDRATED_EXPERIMENTS__`)
 * 2. Fallback when no server data:
 *    - `sync: true`  → `getExperimentParamSync` (localStorage, non-blocking)
 *    - `sync: false` → `useExperimentVariant` (SDK warm cache)
 *
 * Does not auto-expose. Call `reportExperimentExposure` manually.
 */
export function useHydratedExperiment(
  experimentName: string,
  options?: { sync?: boolean; defaultValue?: string },
): string {
  const { sync = false, defaultValue = 'control' } = options ?? {}

  // Server-injected value (from root loader data, available on both server
  // and client because React Router serializes it into the HTML payload)
  const serverValue = useRootExperiments()?.[experimentName]

  // Async fallback via SDK warm cache (always called to satisfy Rules of Hooks)
  const { value: sdkValue } = useExperimentVariant<string>(
    experimentName,
    undefined,
    { autoExpose: false },
  )

  return useSyncExternalStore(
    noopSubscribe,
    // getSnapshot (client)
    () => {
      // 1. Prefer server-injected value
      const hydrated = getHydratedExperiment(experimentName)
      if (hydrated) return hydrated
      // 2. Fallback
      if (sync) return getExperimentParamSync(experimentName) ?? defaultValue
      return sdkValue ?? defaultValue
    },
    // getServerSnapshot (server SSR)
    () => serverValue ?? defaultValue,
  )
}

function shouldExposeVariant(value: unknown): boolean {
  // 'off' is Amplitude Experiment's sentinel value for users not
  // targeted by the experiment. Mirror `useExperimentExposure` in
  // `experimentNew.ts` so hydrated callers don't accidentally report
  // exposures for non-participants.
  return (
    value !== null && value !== undefined && value !== '' && value !== 'off'
  )
}

/**
 * SSR-safe counterpart to `useExperimentVariant`.
 *
 * Use this from render paths that affect SSR HTML / first hydrate
 * structure. It reads the same server-injected snapshot as
 * `useHydratedExperiment`, but preserves the `useExperimentVariant`
 * caller contract (`{ value, enableExposure }`) so existing business
 * experiment hooks can migrate without changing their public API.
 *
 * Exposure semantics intentionally match the old hook:
 * - `autoExpose` defaults to true.
 * - `enableExposure()` is idempotent and can be used by callers that
 *   only want to expose after the user actually sees the experiment.
 * - default fallback values are not exposed. We only expose a real
 *   server-injected / SDK-resolved value.
 */
export function useHydratedExperimentVariant<
  T extends string | undefined | null = string,
>(
  experimentName: string,
  defaultValue?: T,
  opt?: { sync?: boolean; autoExpose?: boolean },
): { value: T; enableExposure: () => void } {
  const { sync = false } = opt ?? {}
  const serverValue = useRootExperiments()?.[experimentName] as T | undefined

  // Async fallback via SDK warm cache. Auto-exposure stays disabled here
  // because this wrapper owns exposure after it chooses the final value.
  const { value: sdkValue } = useExperimentVariant<T>(
    experimentName,
    undefined as T,
    { autoExpose: false },
  )

  const value = useSyncExternalStore(
    noopSubscribe,
    () => {
      const hydrated = getHydratedExperiment(experimentName) as T | undefined
      if (hydrated) return hydrated
      if (sync) {
        return ((getExperimentParamSync(experimentName) as T | undefined) ??
          defaultValue) as T
      }
      return (sdkValue ?? defaultValue) as T
    },
    () => (serverValue ?? defaultValue) as T,
  )

  const exposedRef = useRef(false)
  const [exposureEnabled, setExposureEnabled] = useState(
    opt?.autoExpose ?? true,
  )

  useEffect(() => {
    exposedRef.current = false
  }, [experimentName])

  const enableExposure = useCallback(() => {
    setExposureEnabled(true)
  }, [])

  const actualVariantForExposure =
    (typeof window === 'undefined'
      ? serverValue
      : (getHydratedExperiment(experimentName) as T | undefined)) ?? sdkValue

  useEffect(() => {
    if (!exposureEnabled) return
    if (exposedRef.current) return
    if (!shouldExposeVariant(actualVariantForExposure)) return
    reportExperimentExposure(experimentName)
    exposedRef.current = true
  }, [actualVariantForExposure, exposureEnabled, experimentName])

  return { value, enableExposure }
}

// ============================================================================
// dynamicWithExperiment
// ============================================================================

type ModuleLoader<Props> =
  | (() => Promise<LoadModuleResult<Props>>)
  | (() => JSX.Element)

interface DynamicWithExperimentOptions<Props = {}> {
  /** Amplitude experiment name */
  experimentName: string
  /**
   * Treatment implementation.
   * - `() => import(...)` — lazy-loaded with Suspense (CSR-friendly)
   * - `() => <Component />` — rendered synchronously (SSR direct output)
   */
  treatment: ModuleLoader<Props>
  /**
   * Control implementation. Same semantics as `treatment`.
   */
  control: ModuleLoader<Props>
  /**
   * Fallback strategy when server-injected data is unavailable:
   * - `true`:  `getExperimentParamSync` (localStorage, non-blocking)
   * - `false`: `useExperimentVariant` (SDK warm cache)
   * @default false
   */
  sync?: boolean
}

/** Cache lazy wrappers so React.lazy is called once per loader function. */
const lazyCache = createWeakMap<
  Function,
  React.LazyExoticComponent<ComponentType<any>>
>()

/**
 * Create a component that renders either treatment or control based on
 * the experiment variant. Does not auto-expose.
 *
 * Loaders returning a Promise are wrapped in React.lazy + Suspense.
 * Loaders returning JSX are rendered synchronously (SSR direct output).
 *
 * @example
 * // Async (lazy-loaded)
 * export default dynamicWithExperiment({
 *   experimentName: 'exp2604-home-ssr',
 *   treatment: () => import('src/clientRoutes/_app._index.ssr'),
 *   control:   () => import('src/clientRoutes/_app._index'),
 * })
 *
 * // Sync (SSR direct output)
 * dynamicWithExperiment({
 *   experimentName: 'exp2604-ssr-root-layout',
 *   treatment: () => <TreatmentLayout />,
 *   control:   () => <ControlLayout />,
 * })
 */
export function dynamicWithExperiment<Props = {}>(
  options: DynamicWithExperimentOptions<Props>,
): FC<PropsWithoutRef<Props>> {
  const { experimentName, treatment, control, sync = false } = options

  return function DynamicWithExperiment(props: PropsWithoutRef<Props>) {
    const variant = useHydratedExperiment(experimentName, { sync })
    const loader = variant === 'treatment' ? treatment : control

    // Fast path: previously detected async loader
    const Cached = lazyCache.get(loader)
    if (Cached) {
      return (
        <Suspense>
          <Cached {...(props as any)} />
        </Suspense>
      )
    }

    const result = loader()

    // Async: Promise → create lazy and cache
    // import() is deduped by the bundler, so re-calling in lazy() is free
    if (result != null && typeof (result as any).then === 'function') {
      const Lazy = lazy(() =>
        (loader as () => Promise<LoadModuleResult<Props>>)().then(
          normalizeModule,
        ),
      )
      lazyCache.set(loader, Lazy)
      return (
        <Suspense>
          <Lazy {...(props as any)} />
        </Suspense>
      )
    }

    // Sync: JSX → render directly (SSR direct output)
    return <>{result}</>
  }
}
